import React from "react";
import { CheckCircle2, Download, FileSpreadsheet, History, Loader2, Upload } from "lucide-react";
import { apiStokImporApply, apiStokImporDryRun, apiStokImporKembalikan, apiStokImporRiwayat, apiStokImporTemplate, formatApiErrorDetail } from "@/lib/api";
import { jumlahTerapkan, labelField, nilai, qty, saringBaris, STATUS_LABEL } from "@/lib/imporStok";
import * as W from "@/lib/waktu";

// Owner 2026-10-19 (requirement 71, v2.11): opening stock + BOM from Excel (Owner & Finance; undo: Owner).
// Download the template → fill in → check (preview) → apply. The opening stock is booked as a stock movement.
const errText = (e) => formatApiErrorDetail(e.response?.data?.detail) || e.message;
const GAYA = {
  BARU: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", DIUBAH: "border-cyan-500/30 bg-cyan-500/10 text-cyan-300",
  SAMA: "border-slate-600 bg-slate-700/30 text-slate-400", BERMASALAH: "border-red-500/30 bg-red-500/10 text-red-300",
};
const tombol = "inline-flex items-center gap-2 rounded-lg border border-slate-600 px-4 py-2 text-sm text-slate-200 hover:border-amber-500/50 disabled:opacity-50";

function Baris({ b, judul }) {
  return (
    <div data-testid={`stok-impor-baris-${b.baris}`} className="rounded-lg border border-slate-800 bg-white px-3 py-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <span className={`rounded border px-1.5 py-0.5 font-mono ${GAYA[b.status]}`}>{STATUS_LABEL[b.status] || b.status}</span>
        <span className="font-mono text-amber-300">{judul}</span>
        {b.stok_awal_milli ? <span className="rounded bg-emerald-500/10 px-1.5 text-emerald-300">stok awal {qty(b.stok_awal_milli)}</span> : null}
        <span className="ml-auto font-mono text-slate-500">baris {b.baris}</span>
      </div>
      <div className="mt-1 space-y-0.5">
        {(b.alasan || []).map((a) => <div key={a} className="text-red-300">✕ {a}</div>)}
        {b.perubahan && Object.entries(b.perubahan).map(([f, [lama, baru]]) => (
          <div key={f} className="text-cyan-300"><b>{labelField(f)}</b>: {nilai(f, lama)} → {nilai(f, baru)}</div>))}
      </div>
    </div>
  );
}

function Riwayat({ isOwner, onChanged }) {
  const [rows, setRows] = React.useState(null);
  const [pilih, setPilih] = React.useState(null);
  const [alasan, setAlasan] = React.useState("");
  const [msg, setMsg] = React.useState(null);
  const muat = React.useCallback(() => apiStokImporRiwayat().then(setRows).catch((e) => setMsg(errText(e))), []);
  React.useEffect(() => { muat(); }, [muat]);
  const kembalikan = async () => {
    try {
      const h = await apiStokImporKembalikan(pilih, alasan);
      setMsg(`Dikembalikan: ${h.stok_dibalik} stok awal dibalik, ${h.dinonaktifkan} dinonaktifkan, ${h.dikembalikan} dikembalikan${h.dilewati.length ? `; ${h.dilewati.length} dilewati (${h.dilewati.map((x) => `${x.kode}: ${x.alasan}`).join("; ")})` : ""}.`);
      setPilih(null); setAlasan(""); muat(); onChanged?.();
    } catch (e) { setMsg(errText(e)); }
  };
  if (!rows) return null;
  return (
    <div className="space-y-2">
      <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-200"><History size={14} /> Riwayat impor</h3>
      {msg && <div className="text-sm text-amber-300">{msg}</div>}
      {rows.length === 0 ? <div className="text-sm text-slate-500">Belum ada impor.</div> : (
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="text-left text-xs text-slate-500"><th className="py-1 pr-2">Waktu</th><th className="pr-2">File</th><th className="pr-2">Oleh</th><th className="pr-2">Hasil</th><th /></tr></thead>
          <tbody>{rows.map((r) => (
            <tr key={r.id} className="border-t border-slate-800 align-top">
              <td className="whitespace-nowrap py-1.5 pr-2 text-xs">{W.waktuSedang(r.selesai)}</td>
              <td className="pr-2 text-xs">{r.file}</td><td className="pr-2 text-xs">{r.oleh}</td>
              <td className="pr-2 text-xs">bahan +{r.bahan_baru} / ubah {r.bahan_diubah} · stok awal {r.stok_awal} · BOM +{r.bom_baru} / ubah {r.bom_diubah}{r.status === "DIKEMBALIKAN" && <span className="ml-1 text-amber-300">(dikembalikan)</span>}</td>
              <td>{isOwner && r.bisa_dikembalikan && <button data-testid={`stok-impor-kembalikan-${r.id}`} onClick={() => setPilih(r.id)} className="text-xs text-red-500">Kembalikan</button>}</td>
            </tr>))}</tbody>
        </table></div>
      )}
      {pilih && (
        <div className="flex flex-wrap items-center gap-2">
          <input value={alasan} onChange={(e) => setAlasan(e.target.value)} placeholder="Alasan mengembalikan (wajib)" className="min-w-[16rem] flex-1 rounded-lg border border-slate-700 bg-slate-950 px-2 py-1.5 text-sm" />
          <button disabled={alasan.trim().length < 3} onClick={kembalikan} className="rounded-lg bg-red-600 px-3 py-1.5 text-sm text-white disabled:opacity-50">Kembalikan impor</button>
          <button onClick={() => setPilih(null)} className="text-sm text-slate-400">Batal</button>
        </div>
      )}
    </div>
  );
}

export default function ImportStokPanel({ isOwner, onChanged }) {
  const [file, setFile] = React.useState(null);
  const [plan, setPlan] = React.useState(null);
  const [hasil, setHasil] = React.useState(null);
  const [busy, setBusy] = React.useState(null);
  const [error, setError] = React.useState(null);
  const [filter, setFilter] = React.useState(null);
  const [kunci, setKunci] = React.useState(0);
  const unduh = async () => {
    setBusy("unduh"); setError(null);
    try {
      const { blob, nama } = await apiStokImporTemplate();
      const url = URL.createObjectURL(blob); const a = document.createElement("a");
      a.href = url; a.download = nama; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };
  const periksa = async () => {
    setBusy("periksa"); setError(null); setHasil(null);
    try { setPlan(await apiStokImporDryRun(file)); } catch (e) { setError(errText(e)); setPlan(null); } finally { setBusy(null); }
  };
  const terapkan = async () => {
    setBusy("terapkan"); setError(null);
    try { setHasil(await apiStokImporApply(plan.token)); setPlan(null); setFile(null); setKunci((k) => k + 1); onChanged?.(); }
    catch (e) { setError(errText(e)); } finally { setBusy(null); }
  };
  const ring = plan?.ringkasan;
  return (
    <div data-testid="stok-impor" className="space-y-4 rounded-xl border border-slate-800 bg-slate-900/60 p-4">
      <div>
        <h2 className="flex items-center gap-2 font-semibold text-slate-100"><FileSpreadsheet size={18} /> Impor stok awal &amp; BOM dari Excel</h2>
        <p className="mt-1 text-sm text-slate-400">Unduh template (berisi bahan dan BOM yang sudah ada), isi <b>stok awal</b>, harga beli, dan BOM, lalu periksa dan terapkan. Stok awal dicatat sebagai mutasi <b>Stok awal</b> sekali per bahan; bahan yang sudah punya mutasi memakai opname. Data lama dibackup otomatis{isOwner ? " dan bisa dikembalikan" : "; Owner bisa mengembalikan impor"}.</p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <button data-testid="stok-impor-template" disabled={busy} onClick={unduh} className={tombol}>{busy === "unduh" ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} Unduh template</button>
        <input key={`f${kunci}`} data-testid="stok-impor-file" type="file" accept=".xlsx" onChange={(e) => { setFile(e.target.files?.[0] || null); setPlan(null); setHasil(null); }} className="max-w-full text-sm text-slate-300" />
        <button data-testid="stok-impor-periksa" disabled={!file || busy} onClick={periksa} className={tombol}>{busy === "periksa" ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Periksa</button>
      </div>
      {error && <div data-testid="stok-impor-error" className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2.5 text-sm text-red-300">{error}</div>}
      {hasil && (
        <div data-testid="stok-impor-hasil" className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm text-emerald-300">
          <CheckCircle2 size={14} className="mr-1 inline" />Diterapkan: {hasil.bahan_baru} bahan baru{hasil.kode_baru.length ? ` (${hasil.kode_baru.join(", ")})` : ""}, {hasil.bahan_diubah} bahan diubah, {hasil.stok_awal} stok awal, {hasil.bom_baru} BOM baru, {hasil.bom_diubah} BOM diubah.
          {hasil.dilewati.length > 0 && <div className="text-amber-300">Dilewati: {hasil.dilewati.map((x) => `${x.sheet} baris ${x.baris} ${x.kode || ""}: ${x.alasan}`).join("; ")}</div>}
        </div>
      )}
      {plan && (
        <div data-testid="stok-impor-pratinjau" className="space-y-3">
          <div className="grid gap-2 sm:grid-cols-2">
            {[["Bahan", ring.bahan], ["BOM", ring.bom]].map(([j, r]) => (
              <div key={j} className="rounded-lg border border-slate-800 px-3 py-2 text-sm">
                <b className="text-slate-100">{j}:</b> {Object.entries(r).map(([s, n]) => (
                  <button key={s} onClick={() => setFilter(filter === s ? null : s)} className={`ml-2 rounded border px-1.5 text-xs ${GAYA[s]} ${filter === s ? "ring-2 ring-amber-400" : ""}`}>{STATUS_LABEL[s]} {n}</button>))}
              </div>))}
          </div>
          <div className="text-sm text-slate-300">Stok awal yang akan dicatat: <b>{ring.stok_awal}</b> bahan. Baris Bermasalah tidak diterapkan.</div>
          <div className="space-y-1.5">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Sheet Bahan</div>
            {saringBaris(plan.bahan, filter).map((b) => <Baris key={`b${b.baris}`} b={b} judul={b.kode || `(kode otomatis) ${b.nama || ""}`} />)}
            <div className="pt-2 text-xs font-semibold uppercase tracking-wider text-slate-500">Sheet BOM</div>
            {saringBaris(plan.bom, filter).map((b) => <Baris key={`m${b.baris}`} b={b} judul={`${b.barang || "?"} ← ${b.bahan || "?"}`} />)}
          </div>
          <button data-testid="stok-impor-terapkan" disabled={busy || !jumlahTerapkan(plan) && !ring.stok_awal} onClick={terapkan}
            className="inline-flex items-center gap-1.5 rounded-lg bg-amber-500 px-4 py-2 text-sm font-semibold text-slate-950 disabled:opacity-50">
            {busy === "terapkan" ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Terapkan {jumlahTerapkan(plan)} baris</button>
        </div>
      )}
      <Riwayat key={`r${kunci}`} isOwner={isOwner} onChanged={onChanged} />
    </div>
  );
}
